import { useState, useEffect } from "react";

interface MisidentifiedTypeDialogProps {
  open: boolean;
  onConfirm: (note: string) => void;
  onCancel: () => void;
}

/**
 * R-247 5b-3b — keyer flags the open doc as the wrong document type. One click +
 * optional note; warns that re-classification clears keyed values.
 *
 * Purely presentational — does NOT call any API. The Verification Station wires
 * the POST in Task 6. Matches the local VS-dialog convention (inline <style>
 * block + dark Industrial Core tokens, see OverrideDialog.tsx).
 */
export function MisidentifiedTypeDialog({ open, onConfirm, onCancel }: MisidentifiedTypeDialogProps) {
  const [note, setNote] = useState("");

  // Reset the note each time the dialog (re)opens — the parent keeps this
  // component mounted, so stale text would otherwise persist across cancels.
  useEffect(() => {
    if (open) setNote("");
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="misident-overlay"
      role="dialog"
      aria-labelledby="misident-title"
      tabIndex={-1}
      onClick={onCancel}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          onCancel();
        }
      }}
    >
      <div className="misident-dialog" onClick={(e) => e.stopPropagation()}>
        <h2 id="misident-title" className="misident-title">Wrong document type?</h2>
        <p className="misident-warning">
          This sends the document back to classification. It will clear the values keyed against the current type.
        </p>
        <label className="misident-label" htmlFor="misident-note">What should it be? (optional)</label>
        <textarea
          id="misident-note"
          className="misident-note"
          placeholder="What should it be? / why"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
        />
        <div className="misident-actions">
          <button type="button" className="misident-btn misident-btn--cancel" onClick={onCancel}>Cancel</button>
          <button type="button" className="misident-btn misident-btn--confirm" onClick={() => onConfirm(note.trim())}>Flag wrong type</button>
        </div>
      </div>
      <style>{`
        .misident-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 500; }
        .misident-dialog { position: relative; background: var(--color-bg-secondary, #131313); border: 1px solid var(--color-border, #2a2a2a); border-radius: 6px; padding: 16px; width: 420px; max-width: 90vw; }
        .misident-dialog::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; background: var(--color-primary, #ea580c); border-radius: 6px 0 0 6px; }
        .misident-title { margin: 0 0 8px; font-size: 0.95rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: var(--color-text-primary, #e5e5e5); }
        .misident-warning { margin: 0 0 12px; font-size: 0.8rem; color: var(--color-text-secondary, #a3a3a3); }
        .misident-label { display: block; font-size: 0.7rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: var(--color-text-muted, #737373); margin-bottom: 4px; }
        .misident-note { width: 100%; box-sizing: border-box; font: inherit; font-size: 0.8rem; padding: 8px; background: var(--color-bg-primary, #0e0e0e); color: var(--color-text-primary, #e5e5e5); border: 1px solid var(--color-border, #2a2a2a); border-radius: 4px; resize: vertical; }
        .misident-note:focus { outline: none; box-shadow: 0 0 0 3px rgba(252, 148, 48, 0.35); border-color: var(--color-primary, #ea580c); }
        .misident-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px; }
        .misident-btn { padding: 8px 14px; border-radius: 4px; font: inherit; font-size: 0.8rem; cursor: pointer; border: 1px solid var(--color-border, #2a2a2a); transition: background 0s, color 0s, border-color 0s; }
        .misident-btn--cancel { background: transparent; color: var(--color-text-secondary, #a3a3a3); }
        .misident-btn--cancel:hover { color: var(--color-text-primary, #e5e5e5); border-color: var(--color-text-muted, #737373); }
        .misident-btn--confirm { background: var(--color-primary, #ea580c); color: #fff; border-color: var(--color-primary, #ea580c); }
        .misident-btn--confirm:hover { filter: brightness(1.1); }
      `}</style>
    </div>
  );
}
